import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { RecursiveMatSelectInterface, RecursiveSelectedInterface } from '../../../../models/filter.model';
import { forkJoin, Observable } from 'rxjs';
import { ProgramFilterService } from '../../../../components/webinar-course/program-filter.service';

@Component({
    selector: 'app-recursive-mat-select',
    templateUrl: './recursive-mat-select.component.html',
})
export class RecursiveMatSelectComponent implements OnChanges {
    @Input() data: RecursiveMatSelectInterface[] = [];
    @Input() topicToBeRemove = '';
    @Input() topicToBeSelected = '';
    @Input() disabledData: string[] = [];
    @Input() isTopic = false;
    @Output() selectionChange: EventEmitter<RecursiveSelectedInterface[]> = new EventEmitter<
        RecursiveSelectedInterface[]
    >();
    @Output() isClicked = new EventEmitter<void>();

    protected selectedData: RecursiveSelectedInterface[] = [];

    constructor(private programFilterService: ProgramFilterService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['topicToBeRemove']) {
            const item = this.programFilterService.getFilterData(this.topicToBeRemove, this.data);

            if (item) {
                this.onFilterChange(item);
            }
        }

        if (changes['topicToBeSelected']) {
            const item = this.programFilterService.getFilterData(this.topicToBeSelected, this.data);

            if (item) {
                this.onFilterChange(item, undefined, true);
            }
        }

        if (changes['disabledData']) {
            this.getSelectedData();
        }
    }

    toggleExpand(item: RecursiveMatSelectInterface): void {
        item.expanded = !item.expanded;
    }

    onFilterChange(item: RecursiveMatSelectInterface, event?: MatCheckboxChange, forceCheck?: boolean): void {
        const isChecked = forceCheck ?? event?.checked ?? false;

        this.selectCurrentItem(item);

        forkJoin([this.expandAllChildren(item, isChecked), this.checkParentSelection(item, isChecked)]).subscribe({
            next: () => {
                this.selectionChange.emit(this.getSelectedData());

                if (event) {
                    this.isClicked.emit();
                }
            },
        });
    }

    private selectCurrentItem(item: RecursiveMatSelectInterface): void {
        item.selected = !item.selected;
    }

    private checkParentSelection(item: RecursiveMatSelectInterface, isChecked: boolean): Observable<void> {
        return new Observable<void>((observer) => {
            const parent = this.findParent(item, this.data);

            if (parent) {
                if (isChecked) {
                    if (!parent.disabled) {
                        parent.selected = true;
                    }
                } else {
                    parent.selected = parent.children.some((child) => child.selected);
                }

                this.checkParentSelection(parent, parent.selected).subscribe();
            }

            observer.next();
            observer.complete();
        });
    }

    private findParent(
        item: RecursiveMatSelectInterface,
        list: RecursiveMatSelectInterface[],
    ): RecursiveMatSelectInterface | null {
        for (const listItem of list) {
            if (listItem.children && listItem.children.includes(item)) {
                return listItem;
            } else if (listItem.children && listItem.children.length > 0) {
                const parent = this.findParent(item, listItem.children);

                if (parent) {
                    return parent;
                }
            }
        }

        return null;
    }

    private getSelectedData(): RecursiveSelectedInterface[] {
        const selectedData: RecursiveSelectedInterface[] = [];

        const gatherSelected = (items: RecursiveMatSelectInterface[]): RecursiveSelectedInterface[] => {
            return items.reduce((acc: RecursiveSelectedInterface[], item: RecursiveMatSelectInterface) => {
                if (item.selected) {
                    const selectedItem: RecursiveSelectedInterface = {
                        id: item.id,
                        name: item.name,
                        filterType: item.filterType,
                        parentId: [],
                        childId: [],
                    };

                    const gatherAllParentIds = (currentItem: RecursiveMatSelectInterface): string[] => {
                        const parent = this.findParent(currentItem, this.data);

                        if (parent) {
                            parent.expanded = true;

                            return [parent.id, ...gatherAllParentIds(parent)];
                        } else {
                            return [];
                        }
                    };

                    selectedItem.parentId = gatherAllParentIds(item);

                    const gatherAllChildIds = (children: RecursiveMatSelectInterface[]): string[] => {
                        return children.reduce((childAcc: string[], child: RecursiveMatSelectInterface) => {
                            if (child.selected) {
                                childAcc.push(child.id);
                            }

                            if (child.children && child.children.length > 0) {
                                childAcc.push(...gatherAllChildIds(child.children));
                            }

                            return childAcc;
                        }, []);
                    };

                    if (item.children && item.children.length > 0) {
                        const selectedChildrenIds = gatherAllChildIds(item.children);

                        selectedItem.childId = selectedChildrenIds.length > 0 ? selectedChildrenIds : undefined;
                    }

                    acc.push(selectedItem);
                }

                if (item.children && item.children.length > 0) {
                    acc.push(...gatherSelected(item.children));
                }

                return acc;
            }, []);
        };

        selectedData.push(...gatherSelected(this.data));

        this.selectedData = selectedData;

        return selectedData;
    }

    private expandAllChildren(item: RecursiveMatSelectInterface, isChecked: boolean): Observable<void> {
        return new Observable<void>((observer) => {
            item.expanded = true;

            if (item.children && item.children.length > 0) {
                item.children.forEach((child) => {
                    child.expanded = true;

                    if (!child.disabled) {
                        child.selected = isChecked;
                    }

                    this.expandAllChildren(child, isChecked).subscribe();
                });
            }

            observer.next();
            observer.complete();
        });
    }
}
